import { Component, computed, effect, inject, input, untracked } from '@angular/core';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Router } from '@angular/router';
import { environment } from '@core/environments/environment';
import { TranslateModule } from '@ngx-translate/core';
import { EAiConnectorSource } from '@shared/components/ai-connector-dialog/ai-connector-dialog.interface';
import { AiConnectorService } from '@shared/components/ai-connector-dialog/ai-connector.service';
import { IconBadgeComponent } from '@shared/components/icon-badge/icon-badge.component';
import { IconComponent } from '@shared/components/icon/icon.component';

/** "New" teaser above the sidebar's user footer - see docs/features/ai-connector-profile-and-teaser.md. */
@Component({
  imports: [IconBadgeComponent, IconComponent, MatTooltipModule, TranslateModule],
  selector: 'app-ai-connector-teaser',
  templateUrl: './ai-connector-teaser.component.html',
})
export class AiConnectorTeaserComponent {
  public readonly expanded = input.required<boolean>();

  public readonly aiConnectorService = inject(AiConnectorService);
  private readonly router = inject(Router);
  public readonly textKey = environment.aiConnectorChatGptEnabled
    ? 'shared.aiConnector.teaser.text'
    : 'shared.aiConnector.teaser.textWithoutChatGpt';

  private readonly source = computed(() =>
    this.expanded() ? EAiConnectorSource.sidebarTeaser : EAiConnectorSource.sidebarTeaserCollapsed,
  );

  constructor() {
    effect(() => {
      if (this.aiConnectorService.teaserVisible()) {
        // Only visibility re-runs this: toggling the sidebar must not count as another view.
        untracked(() => this.aiConnectorService.trackTeaserViewed(this.source()));
      }
    });
  }

  /** Profile is where the "AI assistants" box lives, so the user lands there once the modal closes. */
  public async onOpenClick(): Promise<void> {
    // Open only after navigation succeeds - a canDeactivate guard (unsaved changes) may cancel it
    // and show its own confirm dialog, which ours must not stack on top of. Navigating to the
    // current URL also resolves false, hence the explicit already-on-profile check.
    const isOnProfile = this.router.url.split(/[?#]/)[0] === '/profile';
    // Read before awaiting: the sidebar may collapse meanwhile (e.g. while a canDeactivate prompt is open).
    const source = this.source();

    if (isOnProfile || (await this.router.navigate(['/profile']))) {
      this.aiConnectorService.openDialog(source);
    }
  }

  public onDismissClick(): void {
    this.aiConnectorService.trackTeaserDismissed(this.source());
    this.aiConnectorService.dismissTeaser();
  }
}
